import { debounce } from 'lodash';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Section } from 'src/components/widgets/section';
import { InputWithValidation } from 'src/shared/globals';
import { cartesianProduct, T, uuid, validateInputSecurity } from 'src/shared/helpers';
import AiOutlinePlus from 'src/shared/material_icons/content/add.svg';
import TrashIcon from 'src/shared/material_icons/action/delete.svg';
import { useValidationContext, validateRequired } from 'src/shared/validation';
import styled from 'styled-components';
import { Button, Link } from '../../../../../widgets/buttons';
import { WizardFooterBackButton } from '../../wizardFooterBackButton';
import { FormControl } from '../../../../../widgets/formControl';
import { PageFooterButtons } from '../../../../../widgets/headers';
import { Icon } from '../../../../../widgets/icon';
import { ContainerBase } from '../../../../../widgets/layouts';
import { addNewColorAction, editColorNameAndAttributesAction, removeColorAction, useProductConfigurationState } from '../../../state';
import SelectedOptionValue from '../../../state/selectedOptionValue';
import { AttributeContainerItem, AttributeOptionList } from './attributeOptionList';

// Styled components

const VariantsSection = styled(Section)`
    /* Fill down to just above the thin fixed footer (no wasted gap). The sticky "Add new attribute"
       bar pins to the bottom of this area, so it stays right above the footer. */
    height: calc(100vh - 150px);
    max-width: 1400px;
    margin-left: auto;
    margin-right: auto;
    overflow-y: auto;
`;

const LinkRemoveAttribute = styled(Link)`
    display: flex;
    align-self: start;
    justify-self: end;
    ${Icon} {
        color: ${(props) => props.theme.app.primary};
        svg {
            height: 24px;
            width: 24px;
        }
    }
`;

const LinkAddButtonContainer = styled.div<{ $stuck?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 14px;
    /* Fixed-height bar (independent of its content). */
    height: 52px;
    min-height: 52px;
    flex-shrink: 0;
    box-sizing: border-box;
    margin-top: 13px;

    /* Scrolls normally with the list; only pins to the bottom once the list overflows the section
       (so it doesn't float with a gap when the content is short). The attribute divider above already
       provides the visual separation, so the sticky bar itself has no border. */
    ${(props) =>
        props.$stuck &&
        `
        position: sticky;
        bottom: 0;
        z-index: 2;
        background: #fff;
    `}

    a {
        display: flex;
        align-items: center;
        text-decoration: none;
        color: ${(props) => props.theme.app.secondary};
        svg {
            margin-right: 5px;
        }
    }
`;

const VariantSummary = styled.div`
    ul {
        padding: 0 20px;
        list-style-type: decimal;

        > li {
            margin-bottom: 5px;
            label {
                &:after {
                    content: ' - ';
                }

                &:last-child:after {
                    content: '';
                }
            }
        }
    }
`;

const MovedIcon = styled(Icon)<{ movable?: boolean }>`
    vertical-align: middle;
    ${(props) =>
        props.movable &&
        !props.disabled &&
        `&:hover {
          cursor: grab;
          color: ${props.theme.app.secondary}
      }`}
`;

// Ensures the given attribute always has exactly one trailing empty option,
// so a new empty slot appears automatically while typing (no need to click "+").
const withTrailingEmptyOption = (list: SelectedOptionValue[][], attributeId: string): SelectedOptionValue[][] => {
    if (list.length === 0) return list;
    const attrIndex = list[0].findIndex((a) => a.Id === attributeId);
    if (attrIndex < 0) return list;

    const options = [...new Set(list.map((m) => JSON.stringify(m[attrIndex])))].map((m2) => JSON.parse(m2) as SelectedOptionValue);
    if (options.some((o) => o.Value.Label.trim() === '')) return list;

    if (list[0].length === 1) {
        return [...list, [{ Id: attributeId, Label: list[0][0].Label, Value: { Id: uuid(), Label: '' } }]];
    }

    const attributesAcc: SelectedOptionValue[][] = [];
    for (let i = 0; i < list[0].length; i++) {
        let elements = [...new Set(list.map((m) => JSON.stringify(m[i])))].map((m2) => JSON.parse(m2) as SelectedOptionValue);
        if (list[0][i].Id === attributeId) elements = [...elements, { Id: attributeId, Label: list[0][i].Label, Value: { Id: uuid(), Label: '' } }];
        attributesAcc.push(elements);
    }
    return cartesianProduct(...attributesAcc);
};

const EditVariantsOffline: FunctionComponent<{ onSaveVariants: () => void }> = ({ onSaveVariants }) => {
    const { state, dispatch, unsavedChanges } = useProductConfigurationState();
    const navigate = useNavigate();
    const validationContext = useValidationContext();

    // Variants set in the product state
    const modelColorCodes = state.product.colors.map((c) => {
        return JSON.parse(c.code || '[]') as SelectedOptionValue[];
    });

    // State
    const [newModelColorCodes, setNewModelColorCodes] = useState<SelectedOptionValue[][]>(() => {
        let initial =
            modelColorCodes[0].length > 0
                ? [...modelColorCodes]
                : modelColorCodes.map((mc) => [...mc, { Id: uuid(), Label: '', Value: { Id: uuid(), Label: '' } }]);
        // Make sure every attribute starts with a trailing empty option so values can be typed without clicking "+".
        if (initial.length > 0) {
            initial[0].forEach((attr) => {
                initial = withTrailingEmptyOption(initial, attr.Id);
            });
        }
        return initial;
    });
    const [isFirstLoad, setIsFirstLoad] = useState(true);

    // Used to scroll a newly added attribute into view and to measure the scroll area.
    const addAttributeLinkRef = useRef<HTMLDivElement>(null);
    // The "Add new attribute" bar only sticks to the bottom once the list overflows the section.
    const [addBarStuck, setAddBarStuck] = useState(false);

    // Nearest scrollable ancestor of the add bar (the VariantsSection scroll container).
    const getScroller = (): HTMLElement | null => {
        let node: HTMLElement | null = addAttributeLinkRef.current?.parentElement ?? null;
        while (node) {
            const overflowY = getComputedStyle(node).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') return node;
            node = node.parentElement;
        }
        return null;
    };

    // Attributes
    const attributes = newModelColorCodes.length == 0 ? [] : newModelColorCodes[0];

    // Toggle the sticky bar based on whether the list overflows the section (re-measure on content
    // change and on resize).
    useEffect(() => {
        const measure = () => {
            const scroller = getScroller();
            if (scroller) setAddBarStuck(scroller.scrollHeight > scroller.clientHeight + 1);
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [newModelColorCodes]);

    // Unsaved changes are surfaced via the shared navigation-guard banner (no modal).
    const handleBackClick = () => {
        if (unsavedChanges.guardNavigation()) return;
        navigate(-1);
    };

    // Callbacks

    // ****** Adding options
    const onAddOption = (attributeId: string) => {
        setNewModelColorCodes(withTrailingEmptyOption([...newModelColorCodes], attributeId));
    };

    // ****** Removing options
    const onRemoveOption = (attributeId: string, optionId: string, optionLabel: string) => {
        let list = [...newModelColorCodes];

        if (list.filter((mc) => mc.some((attr) => attr.Id == attributeId)).length <= 1) return;

        list = list.filter((mc) => !mc.some((attr) => attr.Id == attributeId && attr.Value.Id == optionId));
        if (list.length > 0) setNewModelColorCodes(list);

        let modelColors = [...state.product.colors];
        let newModelColorCodesStringify = list.map((mc) => JSON.stringify(mc));

        modelColors.forEach((c, i) => {
            if (!newModelColorCodesStringify.includes(c.code)) {
                let modelColor = state.product.colors.find((col) => col.code === c.code);
                if (modelColor) dispatch(removeColorAction(modelColor));
            }
        });
    };

    // ****** Edit options
    const onEditOption = (newValue: string, attributeId: string, optionId: string) => {
        let list = [...newModelColorCodes];
        list.forEach((mc) => {
            let attribute = mc.find((attr) => attr.Id === attributeId);
            if (attribute && attribute.Value.Id === optionId) attribute.Value.Label = newValue;
        });
        // Auto-create the next empty option slot so the user never needs to click "+".
        list = withTrailingEmptyOption(list, attributeId);
        setNewModelColorCodes(list);

        let modelColorsCopy = [...state.product.colors];
        list.forEach((mc, i) => {
            // Empty options are not counted — don't sync empty combinations to the product.
            if (mc.some((o) => o.Label === '' || o.Value.Label === '')) return;

            let modelColor = modelColorsCopy.find((c) => {
                let modelColorCode = JSON.parse(c.code) as SelectedOptionValue[];
                return modelColorCode.some((o, i) => o.Id === mc[i].Id && o.Value.Id === mc[i].Value.Id);
            });

            if (modelColor) {
                let nameVariant = mc.map((o) => o.Value.Label).join(' - ');
                dispatch(editColorNameAndAttributesAction(modelColor, nameVariant, mc));
            } else {
                dispatch(addNewColorAction(mc));
            }

            modelColorsCopy = modelColorsCopy.filter((mc) => modelColor && mc.guid != modelColor.guid);
        });
    };

    // ****** Edit attribute
    const onEditAttribute = (newAttributeValue: string, attributeId: string) => {
        let list = [...newModelColorCodes];
        list.forEach((mc) => {
            let attribute = mc.find((attr) => attr.Id === attributeId);
            if (attribute) attribute.Label = newAttributeValue;
        });
        setNewModelColorCodes(list);

        let modelColorsCopy = [...state.product.colors];
        list.forEach((mc, i) => {
            // Empty options are not counted — don't sync empty combinations to the product.
            if (mc.some((o) => o.Label === '' || o.Value.Label === '')) return;

            let modelColor = modelColorsCopy.find((c) => {
                let modelColorCode = JSON.parse(c.code) as SelectedOptionValue[];
                return modelColorCode.some((o, i) => o.Id === mc[i].Id && o.Value.Id === mc[i].Value.Id);
            });

            if (modelColor) {
                let nameVariant = mc.map((o) => o.Value.Label).join(' - ');
                dispatch(editColorNameAndAttributesAction(modelColor, nameVariant, mc));
            } else {
                dispatch(addNewColorAction(mc));
            }

            modelColorsCopy = modelColorsCopy.filter((mc) => modelColor && mc.guid != modelColor.guid);
        });
    };

    // ****** Adding new attribute
    const onAddNewAttribute = () => {
        // Adding a fresh (empty) attribute must not immediately flag it (or the others) red — the red
        // outline should only reappear on the next Apply.
        validationContext.resetSubmitted();

        let list = [...newModelColorCodes];
        // if (list.some(attr=>attr.some(value => value.Label == '')))
        //   return;

        list = cartesianProduct(list, [{ Id: uuid(), Label: '', Value: { Id: uuid(), Label: '' } }]);
        setNewModelColorCodes(list);

        // Reveal the freshly added attribute by scrolling the list to the bottom — but ONLY when it
        // actually overflows (so nothing moves when there's still room). Double rAF so the new
        // attribute has rendered first.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const scroller = getScroller();
                if (scroller && scroller.scrollHeight > scroller.clientHeight) {
                    scroller.scrollTo({ top: scroller.scrollHeight, behavior: 'smooth' });
                }
            });
        });
    };

    // ***** Removing attribute
    const onRemoveAttribute = (attribute: SelectedOptionValue) => {
        if (newModelColorCodes[0].length == 0) return;

        let list = [...newModelColorCodes];
        let indexAttributeToRemove = attributes.findIndex((a) => a.Id === attribute.Id);

        if (indexAttributeToRemove >= 0) {
            list.forEach((mp) => mp.splice(indexAttributeToRemove, 1));

            list = [...new Set(list.map((m) => JSON.stringify(m)))].map((m2) => JSON.parse(m2));
            setNewModelColorCodes(list);

            let modelColorsCopy = [...state.product.colors];
            list.forEach((mc, i) => {
                let modelColor = modelColorsCopy.find((c) => {
                    let modelColorCode = (JSON.parse(c.code) || []) as SelectedOptionValue[];
                    if (modelColorCode.length == 0) return false;

                    if (modelColorCode.length != mc.length)
                        return modelColorCode.some((o, i) => {
                            if (i >= mc.length) return true;
                            return o.Id === mc[i].Id && o.Value.Id === mc[i].Value.Id;
                        });

                    return false;
                });

                // Aggiorno il model color
                if (modelColor) {
                    let nameVariant = mc.map((o) => o.Value.Label).join(' - ');
                    dispatch(editColorNameAndAttributesAction(modelColor, nameVariant, mc));
                    modelColorsCopy = modelColorsCopy.filter((mc) => modelColor && modelColor.guid != mc.guid);
                }
            });

            let newModelColorsState = [...state.product.colors];

            if (attribute.Label != '') {
                // Rimuovo quelli non più necessari
                modelColorsCopy.forEach((mc) => {
                    let modelColorCode = (JSON.parse(mc.code) || []) as SelectedOptionValue[];
                    if (modelColorCode.length >= list[0].length) {
                        let modelColorToRemove = newModelColorsState.find((c) => c.code === mc.code);

                        if (modelColorToRemove) dispatch(removeColorAction(modelColorToRemove));

                        newModelColorsState = newModelColorsState.filter((mc) => modelColorToRemove && mc.guid != modelColorToRemove.guid);
                    }
                });
            }
        }
    };

    // **** Confirm and save variants in the product state
    const onConfirmAndSaveVariants = () => {
        setIsFirstLoad(false);

        // Source-of-truth check: every attribute must have a name AND at least one filled option (empty
        // options — including the trailing one — are ignored). We decide whether to proceed from the data
        // itself, so a valid setup never blocks; the standard validation (submit) is only used to surface
        // the toast + red outline when something is actually missing.
        const allAttributesValid = attributes.every((attr, i) => {
            const nameOk = (attr.Label ?? '').trim() !== '' && validateInputSecurity(attr.Label).isValid;
            const hasFilledOption = newModelColorCodes.some((row) => {
                const label = row[i]?.Value.Label ?? '';
                return label.trim() !== '' && validateInputSecurity(label).isValid;
            });
            return nameOk && hasFilledOption;
        });

        if (!allAttributesValid) {
            validationContext.submit();
            return;
        }

        onSaveVariants();
    };

    const onChangeAttributeValue = debounce(onEditAttribute, 10);
    const handleInputAttributeChange = (e: React.ChangeEvent<HTMLInputElement>, attributeId: string) => {
        onChangeAttributeValue(e.currentTarget.value, attributeId);
    };

    // ** Body render component
    return (
        <ContainerBase>
            <VariantsSection>
                {/* <DndContext>
      <SortableContext 
        strategy={verticalListSortingStrategy}
        items={attributes.map(m=>m.Id)}> */}
                {attributes.length > 0 &&
                    attributes.map((attribute, index) => {
                        // The attribute is valid only with a name AND at least one filled option
                        // (a trailing empty option is tolerated).
                        const hasFilledOption = newModelColorCodes.some((row) => (row[index]?.Value.Label ?? '').trim() !== '');
                        return (
                            <AttributeContainerItem itemID={attribute.Id} key={`Attribute-${index}`}>
                                <FormControl label='Attribute name' caption={T._('Enter your attribute title, such as "color" or "size"', 'Admin')}>
                                    <InputWithValidation
                                        message={`${!validateInputSecurity(attribute.Label).isValid ? T._(`Invalid characters detected: ${validateInputSecurity(attribute.Label).invalidCharacters.join(', ')}`, 'Admin') : validateRequired(attribute.Label, true) ? T._('Please enter the attribute name', 'Admin') : ''}`}
                                        value={attribute.Label}
                                        onChange={(e) => handleInputAttributeChange(e, attribute.Id)}
                                        placeholder={T._('Attribute name', 'Admin')}
                                        validationKey={`AttributeValidation-${attribute.Id}-${index}`}
                                        validate={(value) => validateRequired(value, true) || !validateInputSecurity(value).isValid}
                                    />
                                </FormControl>
                                <FormControl
                                    label={T._('Attribute values', 'Admin')}
                                    caption={T._('Enter the options for the attribute, such as "red" for "color" or "medium" for "size"', 'Admin')}
                                >
                                    <AttributeOptionList
                                        isFirstLoad={isFirstLoad}
                                        isAddVisible={false}
                                        requireOption={!hasFilledOption}
                                        onRemoveOption={onRemoveOption}
                                        onEditOption={onEditOption}
                                        onAddOption={onAddOption}
                                        values={[...new Set(newModelColorCodes.map((m) => JSON.stringify(m[index])))].map((m2) => JSON.parse(m2))}
                                    />
                                </FormControl>
                                {newModelColorCodes[0].length > 1 && (
                                    <LinkRemoveAttribute onClick={() => onRemoveAttribute(attribute)}>
                                        <Icon>
                                            <TrashIcon />
                                        </Icon>
                                    </LinkRemoveAttribute>
                                )}
                            </AttributeContainerItem>
                        );
                    })}
                {/* </SortableContext>
      </DndContext> */}

                {/* Link add new attribute */}
                <LinkAddButtonContainer ref={addAttributeLinkRef} $stuck={addBarStuck}>
                    <Link onClick={onAddNewAttribute}>
                        <Icon>
                            <AiOutlinePlus></AiOutlinePlus>
                        </Icon>
                        {T._('Add new attribute', 'Admin')}
                    </Link>
                </LinkAddButtonContainer>

                <PageFooterButtons $fixed={true} fullWidth leftContainerContent={<WizardFooterBackButton onClick={handleBackClick} />}>
                    <Button onClick={onConfirmAndSaveVariants}>{T._('Apply', 'Admin')}</Button>
                </PageFooterButtons>
            </VariantsSection>
        </ContainerBase>
    );
};

export default EditVariantsOffline;
